import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import MovieCard from "@/components/MovieCard";
import { Search, Loader2, SlidersHorizontal } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { v: "all", l: "All" },
  { v: "movie", l: "Movies" },
  { v: "series", l: "TV Series" },
];

export default function Browse() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState(params.get("type") || "all");
  const [genre, setGenre] = useState(params.get("genre") || "all");
  const [year, setYear] = useState(params.get("year") || "all");
  const [actor, setActor] = useState(params.get("actor") || "");

  useEffect(() => {
    api.get("/genres").then((r) => setGenres(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch genres once on mount

  const fetchResults = useCallback(async () => {
    setLoading(true);
    const sp = {};
    if (q) sp.q = q;
    if (type !== "all") sp.type = type;
    if (genre !== "all") sp.genre = genre;
    if (year !== "all") sp.year = year;
    if (actor) sp.actor = actor;
    try {
      const { data } = await api.get("/content", { params: sp });
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [q, type, genre, year, actor]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchResults();
      const sp = {};
      if (q) sp.q = q;
      if (type !== "all") sp.type = type;
      if (genre !== "all") sp.genre = genre;
      if (year !== "all") sp.year = year;
      if (actor) sp.actor = actor;
      setParams(sp, { replace: true });
    }, 350);
    return () => clearTimeout(t);
  }, [q, type, genre, year, actor, fetchResults, setParams]);

  const addToList = async (item) => {
    if (!user) return toast.error("Sign in to add to your list");
    try {
      await api.post("/mylist", { content_id: item.id });
      toast.success(`Added "${item.title}"`);
    } catch { toast.error("Could not add"); }
  };

  const years = ["all", 2025, 2024, 2023, 2022];

  return (
    <div className="bg-ink min-h-screen pt-24 px-4 sm:px-12 pb-20">
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-6">Browse & Search</h1>

      <div className="flex items-center bg-surface border border-white/10 rounded-xl px-4 py-3 mb-4 focus-within:ring-1 focus-within:ring-brand max-w-2xl">
        <Search className="w-5 h-5 text-white/50" />
        <input
          data-testid="browse-search-input"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title, actor, director, genre..."
          className="bg-transparent outline-none px-3 flex-1 text-base placeholder:text-white/40"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="flex items-center gap-2 text-white/50 text-sm"><SlidersHorizontal className="w-4 h-4" /> Filters</div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="filter-type" className="w-32 bg-surface border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-surface border-white/10 text-white">
            {TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={genre} onValueChange={setGenre}>
          <SelectTrigger data-testid="filter-genre" className="w-40 bg-surface border-white/10"><SelectValue placeholder="Genre" /></SelectTrigger>
          <SelectContent className="bg-surface border-white/10 text-white max-h-72">
            <SelectItem value="all">All Genres</SelectItem>
            {genres.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={setYear}>
          <SelectTrigger data-testid="filter-year" className="w-28 bg-surface border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-surface border-white/10 text-white">
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y === "all" ? "Any Year" : y}</SelectItem>)}
          </SelectContent>
        </Select>
        <input
          data-testid="filter-actor"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          placeholder="Actor name"
          className="bg-surface border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-brand w-36"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-brand animate-spin" /></div>
      ) : results.length === 0 ? (
        <p data-testid="no-results" className="text-white/50 py-20 text-center">No titles found. Try different filters.</p>
      ) : (
        <>
          <p className="text-white/50 text-sm mb-4">{results.length} title{results.length !== 1 ? "s" : ""}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {results.map((item) => <MovieCard key={item.id} item={item} onAdd={addToList} fluid />)}
          </div>
        </>
      )}
    </div>
  );
}
